import { SENTRY_BASE_OPTIONS } from '@powerful/config/observability/sentry';
import { createLogger } from '@powerful/config/observability/logger';

/**
 * Observabilidade do app consumidor (§9.10).
 *
 * O bootstrap necessário contém somente o Sentry. O PostHog pertence ao runtime lazy de
 * `analytics.ts`, acionado pelo controller após consentimento de estatísticas. Separar as duas
 * portas impede que analytics volte a entrar no caminho executado antes do opt-in.
 */

export const logger = createLogger({
  nivelMinimo: import.meta.env.DEV ? 'debug' : 'info',
});

/** Só o que esta função lê. `import.meta.env` traz muito mais e não precisa de casar por inteiro. */
export interface ObservabilityEnv {
  VITE_SENTRY_DSN?: string;
}

export interface ObservabilityDeps {
  /** Injetado para permitir teste sem o SDK. Em produção, `Sentry.init`. */
  iniciarSentry: (opcoes: Record<string, unknown>) => void;
  env?: ObservabilityEnv;
}

export interface ObservabilityResult {
  sentry: 'iniciado' | 'sem_dsn' | 'falhou';
}

export function initObservability({
  iniciarSentry,
  // `import.meta.env` é o `ImportMetaEnv` do Vite, mais largo que o que lemos aqui.
  env = import.meta.env as ObservabilityEnv,
}: ObservabilityDeps): ObservabilityResult {
  let sentry: ObservabilityResult['sentry'] = 'sem_dsn';

  if (env.VITE_SENTRY_DSN) {
    try {
      iniciarSentry({
        ...SENTRY_BASE_OPTIONS,
        dsn: env.VITE_SENTRY_DSN,
        environment: import.meta.env.MODE,
      });
      sentry = 'iniciado';
    } catch (erro) {
      // Falha de observabilidade não pode derrubar o produto: o utilizador perde telemetria, não
      // a aplicação. Registar em log é o melhor que se pode fazer aqui.
      logger.warn('Sentry não iniciou', { erro: String(erro) });
      sentry = 'falhou';
    }
  }

  return { sentry };
}
